/*
 * Copyright (c) 2025. Sergey S. Chernov - All Rights Reserved
 *
 *  You may use, distribute and modify this code under the
 * terms of the private license, which you must obtain from the author
 *
 * To obtain the license, contact the author: https://t.me/real_sergeych or email to
 * real dot sergeych at gmail.
 */

package net.sergeych.crypto2

import com.ionspin.kotlin.crypto.util.encodeToUByteArray
import kotlinx.serialization.Serializable
import kotlinx.serialization.Transient
import net.sergeych.bintools.CRC
import net.sergeych.bintools.CRC8
import net.sergeych.crypto2.BinaryId.Companion.createFromBytes
import net.sergeych.crypto2.BinaryId.Companion.createFromString
import net.sergeych.crypto2.BinaryId.Companion.createFromUBytes
import net.sergeych.crypto2.BinaryId.Companion.createRandom
import net.sergeych.crypto2.BinaryId.IncomparableException
import net.sergeych.crypto2.BinaryId.InvalidException
import net.sergeych.mp_tools.decodeBase64Url
import kotlin.random.Random

/**
 * Binary identifier with control code and magic number. To create instaance
 * use one of [createFromBytes], [createFromString], [createFromUBytes],
 * or [createRandom], also deserialize serialized one.
 *
 * Integrity is checked on instantiating automatically.
 *
 * It is comparable to other BinaryId as long as both have the same [magic]. Attempt to
 * compare these that differ throws [IncomparableException]
 *
 * ### Internal structure
 *
 * Say we have a `BinaryId` of size `N` bytes. The inner structure will be:
 *
 * | offset    | meaning |
 * |-----------|---------|
 * | 0 ..< N-2 | id bytes |
 * | N-2       | magic, 0..255 |
 * | N-1       | CRC8, polynomial 0xA7, as in Bluetooth |
 *
 * @throws InvalidException if crc check failed
 */
@Serializable
open class BinaryId(
    /**
     * The packed binary id. Note that serialized version is one byte longer containing
     * the size prefix
     */
    val id: UByteArray,
) : Comparable<BinaryId> {

    /**
     * Bad format (crc does not match)
     */
    class InvalidException(text: String, reason: Throwable? = null) : IllegalArgumentException(text, reason)

    /**
     * Attempt to compare binary ids with different magic. In this case only [equals]
     * works, but [compareTo] throws this exception.
     */
    class IncomparableException(text: String) : IllegalArgumentException(text)

    /**
     * magic number (as decoded), `0..255`
     */
    @Transient
    val magic: Int = run {
        if (id.size < 4) throw InvalidException("BinaryId is too short")
        val crc = id.last()
        val rest = id.dropLast(1).toUByteArray()
        if (CRC.crc8(rest) != crc)
            throw InvalidException("Bad BinaryId CRC")

        rest.last().toInt()
    }

    private val innerData: UByteArray by lazy { id.sliceArray(1..<id.size - 1) }

    /**
     * The ID body: all the bytes except check and magic. ID bytes could carry useful information.
     *
     * - `id.size` is [body] size + 2 (see [BinaryId] inner structure)
     */
    val body: UByteArray by lazy { id.sliceArray(0 until id.size - 2) }

    val asVerifyingKey: VerifyingKey by lazy {
        if (magic != KeysmagicNumber.defaultVerifying.ordinal)
            throw InvalidException("It is not a veryfing key: magic=$magic, required ${KeysmagicNumber.defaultVerifying.ordinal}")
        check(body.size == 32)
        VerifyingPublicKey(body)
    }

    /**
     * Try to recnstruct a [EncryptingPublicKey] from [id] bytes. For such keys, [EncryptingPublicKey.id] and [DecryptingSecretKey.id]
     * are made from public key bytes so it could be restored from such an ID
     *
     */
    val asPublicKey: EncryptingPublicKey by lazy {
        if (magic != KeysmagicNumber.defaultAssymmetric.ordinal)
            throw InvalidException("It is not a veryfing key: magic=$magic, required ${KeysmagicNumber.defaultAssymmetric.ordinal}")
        check(body.size == 32)
        EncryptingPublicKey(body)
    }

    override fun toString(): String = id.encodeToBase64Url()

    /**
     * Compare to another ID which __must have the same [magic]__ number; note that [equals]
     * works well despite magic inequity.
     * @throws IncomparableException if magic id do not match
     */
    override fun compareTo(other: BinaryId): Int {
        if (other.magic != magic) throw IncomparableException("Mask mismatch (my=$magic their=${other.magic})")
        val id1 = other.id
        if (id1.size != id.size) throw IncomparableException("different sizes")
        for ((a, b) in innerData.zip(other.innerData)) {
            if (a < b) return -1
            if (a > b) return 1
        }
        return 0
    }

    override fun equals(other: Any?): Boolean {
        if (this === other) return true
        if (other !is BinaryId) return false

        if (!id.contentEquals(other.id)) return false

        return true
    }

    override fun hashCode(): Int {
        return id.contentHashCode()
    }


    companion object {

        /**
         * Restore a string representation of existing BinaryId.
         */
        @Suppress("unused")
        fun restoreFromString(str: String): BinaryId = kotlin.runCatching {
            BinaryId(str.decodeBase64Url().toUByteArray())
        }.getOrElse { throw InvalidException("can't parse binary id: $str", it) }


        fun createFromBytes(magic: Int, bytes: ByteArray): BinaryId = createFromUBytes(magic, bytes.toUByteArray())

        fun createFromUBytes(magic: Int, bytes: UByteArray): BinaryId {
            val crc = CRC8()
            val mn = magic.toUByte()
            crc.update(bytes)
            crc.update(mn)
            return BinaryId(UByteArray(bytes.size + 2).also {
                bytes.copyInto(it, 0)
                val n = bytes.size
                it[n] = mn
                it[n + 1] = crc.value
            })
        }

        @Suppress("unused")
        fun createRandom(magicNumber: Int, size: Int = 16) =
            createFromBytes(magicNumber, Random.Default.nextBytes(size - 2))

        /**
         * Encode a string as UTF and create a binaryId from its bytes and provided magic.
         */
        fun createFromString(magicNumber: Int, text: String): BinaryId =
            createFromUBytes(magicNumber, text.encodeToUByteArray())
    }
}